import type { components } from '@multaqa/api-client'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { z } from 'zod'
import { api } from '@/lib/api'

export type DepartmentResource = components['schemas']['DepartmentResource']
export type PositionResource = components['schemas']['PositionResource']
export type DepartmentWriteInput = {
  name: Record<string, string>
  code: string
  parent_id?: string | null
  is_active?: boolean
}
export type PositionWriteInput = {
  name: Record<string, string>
  code: string
  is_active?: boolean
}

const localized = z.record(z.string(), z.string())
const departmentSchema = z.object({
  id: z.string(),
  parent_id: z.string().nullable(),
  cost_center_id: z
    .string()
    .nullish()
    .transform((value) => value ?? null),
  name: localized,
  code: z.string(),
  is_active: z.boolean(),
})
const positionSchema = z.object({
  id: z.string(),
  name: localized,
  code: z.string(),
  is_active: z.boolean(),
})

function key(groupId: string, companyId: string, resource: 'departments' | 'positions') {
  return ['platform', 'groups', groupId, 'companies', companyId, resource] as const
}

function bodyOrThrow<T>(body: { data: T } | undefined, endpoint: string): T {
  if (!body) throw new Error(`${endpoint} returned no body`)
  return body.data
}

export function useCompanyDepartments(groupId: string | null, companyId: string | null) {
  return useQuery({
    queryKey: key(groupId ?? '', companyId ?? '', 'departments'),
    enabled: Boolean(groupId && companyId),
    queryFn: async (): Promise<DepartmentResource[]> => {
      const result = await api.GET('/platform/groups/{group}/companies/{company}/departments', {
        params: { path: { group: groupId ?? '', company: companyId ?? '' } },
      })
      return z
        .array(departmentSchema)
        .parse(
          bodyOrThrow(result.data, 'GET /platform/groups/{group}/companies/{company}/departments'),
        )
    },
  })
}

export function useCreateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      companyId,
      input,
    }: {
      groupId: string
      companyId: string
      input: DepartmentWriteInput
    }): Promise<DepartmentResource> => {
      const result = await api.POST('/platform/groups/{group}/companies/{company}/departments', {
        params: { path: { group: groupId, company: companyId } },
        body: input as unknown as components['schemas']['DepartmentStoreRequest'],
      })
      return departmentSchema.parse(
        bodyOrThrow(result.data, 'POST /platform/groups/{group}/companies/{company}/departments'),
      )
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: key(variables.groupId, variables.companyId, 'departments'),
      })
    },
  })
}

export function useUpdateDepartment() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      companyId,
      departmentId,
      input,
    }: {
      groupId: string
      companyId: string
      departmentId: string
      input: Partial<DepartmentWriteInput>
    }): Promise<DepartmentResource> => {
      const result = await api.PATCH(
        '/platform/groups/{group}/companies/{company}/departments/{department}',
        {
          params: { path: { group: groupId, company: companyId, department: departmentId } },
          body: input as unknown as components['schemas']['DepartmentUpdateRequest'],
        },
      )
      return departmentSchema.parse(
        bodyOrThrow(
          result.data,
          'PATCH /platform/groups/{group}/companies/{company}/departments/{department}',
        ),
      )
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: key(variables.groupId, variables.companyId, 'departments'),
      })
    },
  })
}

export function useCompanyPositions(groupId: string | null, companyId: string | null) {
  return useQuery({
    queryKey: key(groupId ?? '', companyId ?? '', 'positions'),
    enabled: Boolean(groupId && companyId),
    queryFn: async (): Promise<PositionResource[]> => {
      const result = await api.GET('/platform/groups/{group}/companies/{company}/positions', {
        params: { path: { group: groupId ?? '', company: companyId ?? '' } },
      })
      return z
        .array(positionSchema)
        .parse(
          bodyOrThrow(result.data, 'GET /platform/groups/{group}/companies/{company}/positions'),
        )
    },
  })
}

export function useCreatePosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      companyId,
      input,
    }: {
      groupId: string
      companyId: string
      input: PositionWriteInput
    }): Promise<PositionResource> => {
      const result = await api.POST('/platform/groups/{group}/companies/{company}/positions', {
        params: { path: { group: groupId, company: companyId } },
        body: input as unknown as components['schemas']['PositionStoreRequest'],
      })
      return positionSchema.parse(
        bodyOrThrow(result.data, 'POST /platform/groups/{group}/companies/{company}/positions'),
      )
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: key(variables.groupId, variables.companyId, 'positions'),
      })
    },
  })
}

export function useUpdatePosition() {
  const queryClient = useQueryClient()
  return useMutation({
    mutationFn: async ({
      groupId,
      companyId,
      positionId,
      input,
    }: {
      groupId: string
      companyId: string
      positionId: string
      input: Partial<PositionWriteInput>
    }): Promise<PositionResource> => {
      const result = await api.PATCH(
        '/platform/groups/{group}/companies/{company}/positions/{position}',
        {
          params: { path: { group: groupId, company: companyId, position: positionId } },
          body: input as unknown as components['schemas']['App.Modules.Platform.Http.Requests.PositionUpdateRequest'],
        },
      )
      return positionSchema.parse(
        bodyOrThrow(
          result.data,
          'PATCH /platform/groups/{group}/companies/{company}/positions/{position}',
        ),
      )
    },
    onSuccess: (_data, variables) => {
      void queryClient.invalidateQueries({
        queryKey: key(variables.groupId, variables.companyId, 'positions'),
      })
    },
  })
}
